import { useEffect, useState } from 'react';
import { getHealth } from '../api/health';

type BadgeState =
  | { kind: 'loading' }
  | { kind: 'ok'; version: string }
  | { kind: 'error' };

/**
 * Shows the live result of `GET /api/healthz` — the visible proof of the
 * iteration-02 slice (API Gateway → Lambda → SPA).
 *
 * Polls once on mount, per the brief. TanStack Query takes over data fetching
 * when the first real domain data arrives (iteration 03+).
 */
export function StatusBadge() {
  const [state, setState] = useState<BadgeState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    getHealth(controller.signal)
      .then((health) => {
        if (active) {
          setState({ kind: 'ok', version: health.version });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: 'error' });
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const label =
    state.kind === 'loading'
      ? 'checking…'
      : state.kind === 'ok'
        ? 'ok'
        : 'unavailable';

  const tone =
    state.kind === 'ok'
      ? 'bg-green-50 text-green-800'
      : state.kind === 'error'
        ? 'bg-red-50 text-red-800'
        : 'bg-gray-50 text-gray-600';

  return (
    <p
      data-testid="status-badge"
      data-status={state.kind}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${tone}`}
    >
      <span aria-hidden="true">API</span>
      <span role="status" aria-live="polite">
        {label}
      </span>
      {state.kind === 'ok' && (
        <span className="font-normal opacity-70">{state.version}</span>
      )}
    </p>
  );
}
